import type { ConnectProto } from "@racoon/api";
import type { FieldSource } from "./ttnDraftUtils";
import type { RouteFieldSources, ShiftFieldSources, TripTimes } from "./useTtnDraft";

/** What the database holds for an approved TTN: its own columns and the scale ticket. */
const isStored = (source: FieldSource | undefined) => source === "saved" || source === "scale";

export type TtnView = {
  shift?: ConnectProto.DriverOnShift;
  routes: ConnectProto.Route[];
  tripTimes: Map<bigint, TripTimes>;
  sources: { shift?: ShiftFieldSources; routes: Map<bigint, RouteFieldSources> };
  returnMileage?: number;
};

/**
 * The TTN as stored, for the PDF: every value the form only fills in on the fly
 * — the plan, the driver's marks, the GPS track, the sensor — is left empty,
 * and so is its border. The way home is never stored, so it is always empty.
 */
export const storedTtnView = <V extends TtnView>(view: V): V => {
  const shiftSources = view.sources.shift ?? {};
  const keep = <T>(value: T, source: FieldSource | undefined) =>
    isStored(source) ? value : undefined;
  const keepSource = (source: FieldSource | undefined) => (isStored(source) ? source : undefined);

  const shift = view.shift && {
    ...view.shift,
    departureLocationId: keep(view.shift.departureLocationId, shiftSources.departureLocationId),
    arrivalLocationId: keep(view.shift.arrivalLocationId, shiftSources.arrivalLocationId),
    departureTime: keep(view.shift.departureTime, shiftSources.departureTime),
    arrivalTime: keep(view.shift.arrivalTime, shiftSources.arrivalTime),
    engineHours: keep(view.shift.engineHours, shiftSources.engineHours),
    mileage: keep(view.shift.mileage, shiftSources.mileage),
  };

  const routeSources = new Map<bigint, RouteFieldSources>();
  const tripTimes = new Map<bigint, TripTimes>();
  const routes = view.routes.map((route) => {
    const sources = view.sources.routes.get(route.id) ?? {};
    routeSources.set(route.id, {
      start: keepSource(sources.start),
      end: keepSource(sources.end),
      place: keepSource(sources.place),
      weight: keepSource(sources.weight),
      mileage: keepSource(sources.mileage),
    });
    const times = view.tripTimes.get(route.id);
    if (times) {
      tripTimes.set(route.id, {
        ...times,
        start: keep(times.start, times.startSource),
        end: keep(times.end, times.endSource),
        startSource: keepSource(times.startSource),
        endSource: keepSource(times.endSource),
      });
    }
    return {
      ...route,
      destinationLocationId: keep(route.destinationLocationId, sources.place),
      weightKg: keep(route.weightKg, sources.weight),
      mileage: keep(route.mileage, sources.mileage),
    } as ConnectProto.Route;
  });

  return {
    ...view,
    shift: shift as ConnectProto.DriverOnShift | undefined,
    routes,
    tripTimes,
    sources: {
      shift: Object.fromEntries(
        Object.entries(shiftSources).map(([field, source]) => [field, keepSource(source)]),
      ) as ShiftFieldSources,
      routes: routeSources,
    },
    returnMileage: undefined,
  };
};
